import { useCallback, useEffect, useState } from "react";
import { Layers, RotateCcw, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { Markdown } from "@/components/ChatMessage";
import { logWarn } from "@/lib/log";
import { ImageViewer } from "@/components/ImageViewer";

function DiagramImg({ id }) {
  const [src, setSrc] = useState(null);
  useEffect(() => { http.get(`/diagrams/${id}/image`, { responseType: "blob" }).then((r) => setSrc(URL.createObjectURL(r.data))).catch((e) => logWarn("Couldn't load card diagram", e)); }, [id]);
  const [open, setOpen] = useState(false);
  if (!src) return <div className="w-full h-40 rounded-xl bg-white/[0.04] mt-4 animate-pulse" />;
  return (
    <>
      <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setOpen(true); }} className="block mt-4 cursor-zoom-in" data-testid="flashcard-diagram-open">
        <img src={src} alt="Card image" decoding="async" className="w-full h-auto max-h-[38vh] object-contain rounded-xl bg-white" data-testid="flashcard-diagram" />
      </span>
      <ImageViewer src={open ? src : null} alt="Card image" onClose={() => setOpen(false)} />
    </>
  );
}

export function FlashcardsPanel() {
  const [data, setData] = useState(null);
  const [i, setI] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const load = useCallback(() => http.get("/flashcards").then((r) => setData(r.data)).catch((e) => logWarn("Couldn't load flashcards", e)), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const due = data.cards.filter((c) => c.due_at <= new Date().toISOString());
  const deck = due.length ? due : data.cards;
  const card = deck[i % Math.max(1, deck.length)];
  const review = async (result) => {
    try { await http.post(`/flashcards/${card.card_id}/review`, { result }); setFlipped(false); setI((x) => x + 1); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const del = async () => { await http.delete(`/flashcards/${card.card_id}`); setFlipped(false); load(); };
  return (
    <div className="mt-14" data-testid="flashcards-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-600 flex items-center gap-2"><Layers className="h-3 w-3" /> Flashcards</p>
        <p className="text-xs text-neutral-500" data-testid="flashcards-due">{data.due} due · {data.cards.length} total</p>
      </div>
      {!card ? (
        <p className="text-sm text-neutral-500 border border-dashed border-white/10 rounded-2xl p-8 text-center">Tap “Make flashcard” under any tutor answer to start your deck.</p>
      ) : (
        <div className="[perspective:1200px]">
          <button onClick={() => setFlipped(!flipped)} data-testid="flashcard"
            className="relative w-full min-h-[min(70dvh,560px)] sm:min-h-[420px] text-left transition-transform duration-[420ms] ease-[cubic-bezier(.2,.7,.2,1)] [transform-style:preserve-3d]" style={{ transform: `perspective(1400px) rotateY(${flipped ? 180 : 0}deg)` }}>
            <div className="absolute inset-0 rounded-2xl border border-white/10 bg-[#121725] p-8 [backface-visibility:hidden] overflow-y-auto">
              <p className="text-xs text-neutral-400 mb-3">{card.topic} · tap to flip</p>
              <div className="text-xl"><Markdown>{card.front}</Markdown></div>
              {card.diagram_id && <DiagramImg id={card.diagram_id} />}
            </div>
            <div className="absolute inset-0 rounded-2xl border border-sky-400/30 bg-sky-500/[0.06] p-8 [backface-visibility:hidden] [transform:rotateY(180deg)] overflow-y-auto" data-testid="flashcard-back">
              <Markdown>{card.back}</Markdown>
            </div>
          </button>
          <div className="flex gap-2 mt-4">
            <button onClick={() => review("again")} className="flex-1 py-2.5 rounded-full border border-yellow-300/30 text-yellow-200 hover:bg-yellow-300/10 flex items-center justify-center gap-2" data-testid="flashcard-again-btn"><RotateCcw className="h-4 w-4" /> Again</button>
            <button onClick={() => review("got")} className="flex-1 py-2.5 rounded-full bg-emerald-400 text-black font-medium hover:bg-emerald-300 flex items-center justify-center gap-2" data-testid="flashcard-got-btn"><Check className="h-4 w-4" /> Got it</button>
            <button onClick={del} className="px-3 rounded-full border border-white/10 text-neutral-500 hover:text-red-400" data-testid="flashcard-delete-btn"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
